import {NgModule, inject} from '@angular/core';
import {RouterModule, Routes, PreloadAllModules, Router} from '@angular/router';
import {catchError, map, of} from 'rxjs';
import {ApplicationContextService} from './services/application-context.service';
import {ChangePasswordComponent} from './components/change-password/change-password.component';
import {TpoChangePasswordComponent} from './components/change-password/tpo-change-password.component';
import {CompanySwitchComponent} from './components/company-switch/company-switch.component';
import {EmailUnsubscribeComponent} from './components/email-unsubscribe/email-unsubscribe.component';
import {ErrorComponent} from './components/error/error.component';
import {ForgotPasswordComponent} from './components/forgot-password/forgot-password.component';
import {LoginComponent} from './components/login/login.component';
import {ResetPasswordComponent} from './components/reset-password/reset-password.component';
import {SettingComponent} from './components/setting/setting.component';
import {ApplicationContextResolver} from './core/resolvers/application-context.resolver';
import {PendingChangesGuard} from './core/route-guards/pending-changes.guard';
import {PopoutRouteLockGuard} from './core/route-guards/popout-route-lock.guard';
import {LayoutAdminComponent} from './layout/admin/admin.component';
import {AmortizationComponent} from './modules/amortization/components/amortization.component';
import {ChangeOfCircumstanceComponent} from './modules/change-of-circumstance/change-of-circumstance.component';
import {ConditionsTasksComponent} from './modules/conditions-tasks/conditions-tasks.component';
import {
  DocumentESigningRequestConfirmationComponent,
} from './modules/document-signing/document-esigning-request-confirmation-component/document-esigning-request-confirmation-component';
import {
  DocumentSigningRequestConfirmationComponent,
} from './modules/document-signing/document-signing-request-confirmation/document-signing-request-confirmation.component';
import {LoanFeesComponent} from './modules/fees/loan-fees.component';
import {FileContactsComponent} from './modules/file-contacts/components/file-contacts.component';
import {InternalContactsComponent} from './modules/internal-contacts/components/internal-contacts.component';
import {KeyDatesComponent} from './modules/key-dates/components/key-dates.component';
import {LoanActivityCardComponent} from './modules/loan-activity/loan-activity-card/loan-activity-card.component';
import {AusComponent, TwnComponent, VoaComponent} from './modules/loan-services';
import {AppraisalComponent} from './modules/loan-services/appraisal/components/appraisal/appraisal.component';
import {DisclosuresWizardComponent} from './modules/loan-services/disclosure/components/disclosures-wizard/disclosures-wizard.component'; 
import {NextGenLayoutComponent} from './modules/loda-nextgen/layout/components/nextgen-layout.component';
import {PricingComponent, PricingHistoryTableComponent} from './modules/pricing';
import {SetupProfileComponent} from './modules/profile/components/setup-profile/setup-profile.component';
import {SubmissionComponent} from './modules/submission';
import {EscalationHistoryComponent} from './modules/tpo/components/escalation-history/escalation-history.component';
import {TpoLoanSummaryComponent} from './modules/tpo/components/loan-summary';
import {TpoAppDetailsComponent} from './modules/tpo/components/tpo-app-details/tpo-app-details.component'; 
import {TpoPipelineComponent} from './modules/tpo/components/tpo-pipeline/tpo-pipeline.component';
import {BrokerSupportComponent} from './modules/tpo/components/broker-support/broker-support.component';
import {TpoQuickPricerComponent} from './modules/tpo/components/tpo-quick-pricer/tpo-quick-pricer.component';
import {TpoSettingsComponent} from './modules/tpo/components/tpo-settings/tpo-settings.component';
import {EmailPreferenceCenterComponent} from './shared/components/email-preference-center/email-preference-center.component';
import {CanActivateAdminRoutesGuard} from './shared/route-guards/can-activate-admin-routes.guard';
import { UnsavedDrawerContentGuard } from './shared/route-guards/unsaved-drawer-content.guard';
import {CanActivateSuperAdminRoutesGuard} from './shared/route-guards/can-activate-super-admin-routes.guard';
import {CanActivateTpoRoutesGuard} from './shared/route-guards/can-activate-tpo-routes.guard';
import {RemainContactComponent} from './modules/remain-contact/components/remain-contact/remain-contact.component';
import {LeadEditorComponent} from './modules/leads/components/lead-editor/lead-editor.component';
import {DocumentPreparationComponent} from './modules/app-details/document-preparation/components/document-preparation.component';
import {
  ApplicationPricingScenariosComponent,
} from './modules/pricing/components/application-pricing-scenarios/application-pricing-scenarios.component';
import {TpoLayoutComponent} from './layout/tpo/tpo-layout/tpo-layout.component';
import {TpoNewApplicationComponent} from './modules/tpo/components/tpo-new-application/tpo-new-application.component';
import {LoanCreditComponent} from './modules/loan-services/loan-credit/components/loan-credit/loan-credit.component';
import {AtrQmManagementComponent} from './modules/app-details/components/atr-qm-management/components/atr-qm-management.component';
import {TpoAppraisalManagementComponent} from './modules/tpo/components/tpo-appraisal-management/tpo-appraisal-management.component';
import {TpoCdRequestComponent} from './modules/tpo/components/tpo-cd-request/tpo-cd-request.component';
import {TpoCorrIcdRequestComponent} from './modules/tpo/components/tpo-corr-icd-request/tpo-corr-icd-request.component';
import {CanActivateBroadcastManagerRoutesGuard} from './modules/broadcast-manager/can-activate-broadcast-manager-route.guard';
import {CanActivateCommandCenterGuard} from './modules/command-center/can-activate-command-center.guard';
import {TpoManualLockComponent} from './modules/tpo/components/tpo-manual-lock/tpo-manual-lock.component';
import { ShortLinkRedirectComponent } from './components/short-link-redirect/short-link-redirect.component';
import { TpoVaPaymentComponent } from './modules/tpo/components/tpo-va-payment/tpo-va-payment.component';
import { ExternalAuthCallbackComponent } from './components/ext-auth-callback/ext-auth-callback.component';
/*
 * Legacy/unused now that SignNow authenticates with a per-company API key set by super admins.
 * The callback route is disabled and unreachable; the component files remain in the tree so this import and
 * the route below can be restored by uncommenting these places.
 * import { SignNowOauthCallbackComponent } from './components/signnow-oauth-callback/signnow-oauth-callback.component';
 */
import { SamlCallbackComponent } from './components/saml-callback/saml-callback.component';
import { ESignViaTokenComponent } from './components/e-sign-via-token/e-sign-via-token.component';
import { ESignViaTokenConfirmationComponent } from './components/e-sign-via-token-confirmation/e-sign-via-token-confirmation.component';
import { ESignVendorConfirmationComponent } from './components/e-sign-vendor-confirmation/e-sign-vendor-confirmation.component';
import { UWDecisionComponent } from './modules/uw-decision/components/uw-decision.component';
import { ExternalContactsV2Component } from './modules/external-contacts-v2/components/external-contacts-v2/external-contacts-v2.component';
import { PricingIFrameChangesGuard } from './core/route-guards/pricing-iframe-changes.guard';
import { TpoEsignatureHistoryComponent } from './modules/tpo/components/tpo-esignature-history/tpo-esignature-history.component';
import { CanActivateAdminRoleRoutesGuard } from './shared/route-guards/can-activate-admin-role-routes.guard';
import { CanActivateDncListAccessGuard } from './shared/route-guards/can-activate-dnc-list-access.guard';
import { CanActivatePipelineMetricsGuard } from './modules/reports/pipeline-metrics/can-activate-pipeline-metrics-routes.guard';
import { CanActivatePipelineWaterfallGuard } from './modules/reports/pipeline-waterfall/can-activate-pipeline-waterfall-routes.guard';
import { MessagingReportMessagePreviewComponent } from './components/messaging-report-message-preview/messaging-report-message-preview.component';
import { TpoLoanSummaryV2Component } from './modules/tpo/components/loan-summary/tpo-loan-summary-v2/tpo-loan-summary-v2.component';
import { MicrosoftLoginRedirectComponent } from './components/microsoft-login-redirect/microsoft-login-redirect.component';
import { TvAuthComponent } from './components/tv-auth/tv-auth.component';
import { DeviceAuthorizeComponent } from './components/device-authorize/device-authorize.component';
import {CanActivateDisabledTpoMenuItemGuard} from './shared/route-guards/can-activate-disabled-tpo-menuItem.routes.guard';
import {CanActivateDuringDisclosureProcessingGuard} from './shared/route-guards/can-activate-during-disclosure-processing.routes.guard';
import {CanActivatePmiQuoteBeforeDisclosuresGuard} from './shared/route-guards/can-activate-pmi-quote-before-disclosures.routes.guard';
import {HasPpeAccessGuard} from './shared/route-guards/has-ppe-access.guard';
import {MiQuotingComponent} from './modules/loan-services/mi-quoting/components/mi-quoting/mi-quoting.component';
import {AccountPausedComponent} from './components/account-paused/account-paused.component';
import {ConversationsPopoutComponent} from './modules/conversations/components/conversations-popout/conversations-popout.component';
import {SendEmailPopoutComponent} from './modules/correspondence/components/send-email-popout/send-email-popout.component';
import {TaskTablePopoutComponent} from './modules/tasks/components/task-table-popout/task-table-popout.component';

// Breadcrumb parent pointing the moved report pages back at the reports hub.
const reportsHubBreadcrumbParent = { label: 'Reporting Hub', url: '/admin/company/reports' };

const routes: Routes = [
  { path: 'switch-company', component: CompanySwitchComponent },
  { path: 'login', component: LoginComponent, data: { appSurface: 'signin' } },
  { path: 'tv-auth', component: TvAuthComponent },
  { path: 'device-authorize', component: DeviceAuthorizeComponent },
  { path: 'error', component: ErrorComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent, data: { appSurface: 'signin' } },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'campaign/unsubscribe', component: EmailUnsubscribeComponent },
  { path: 'email-preference-center', component: EmailPreferenceCenterComponent },
  { path: 'sl/:shortCode', component: ShortLinkRedirectComponent},
  { path: 'ext-auth-callback', component: ExternalAuthCallbackComponent },
  /*
   * Legacy/unused now that SignNow authenticates with a per-company API key set by super admins.
   * The route is disabled and unreachable; uncomment it and the import above to restore the callback.
   * { path: 'signnow-oauth-callback', component: SignNowOauthCallbackComponent },
   */
  { path: 'saml-callback', component: SamlCallbackComponent },
  { path: 'microsoft-login-redirect', component: MicrosoftLoginRedirectComponent },
  {
    path: 'account-paused',
    component: AccountPausedComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
  },
  { path: 'message-report-msg-preview/:companyGuid/:messageGuid', component: MessagingReportMessagePreviewComponent },
  {
    path: 'conversations-popout',
    component: ConversationsPopoutComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    canActivate: [CanActivateAdminRoutesGuard],
    canDeactivate: [PopoutRouteLockGuard],
    data: { appSurface: 'admin' },
  },
  {
    path: 'send-email-popout',
    component: SendEmailPopoutComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    canActivate: [CanActivateAdminRoutesGuard],
    canDeactivate: [PopoutRouteLockGuard],
    data: { appSurface: 'admin' },
  },
  {
    path: 'task-table-popout',
    component: TaskTablePopoutComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    canActivate: [CanActivateAdminRoutesGuard],
    canDeactivate: [PopoutRouteLockGuard],
    data: { appSurface: 'admin' },
  },
  {
    path: 'loda-nextgen',
    component: NextGenLayoutComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    canActivate: [],
    canActivateChild: [],
    data: {
      breadcrumb: 'Home',
    },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./modules/loda-nextgen/nextgen.module').then(
            (m) => m.NextGenModule
          ),
      },
    ],
  },
  {
    path: 'register',
    loadChildren: () =>
      import('./modules/registration/registration.module').then(
        (m) => m.RegistrationModule
      ),
  },
  {
    path: 'open-loan-in-tpo',
    loadChildren: () =>
      import('./modules/open-loan-in-tpo/open-loan-in-tpo.module').then(
        (m) => m.OpenLoanInTpoModule
      ),
  },
  {
    path: 'open-external-org-user-in-tpo',
    loadChildren: () =>
      import('./modules/open-external-org-user-in-tpo/open-external-org-user-in-tpo.module').then(
        (m) => m.OpenExternalOrgUserInTpoModule
      ),
  },
  {
    path: 'impersonate-user',
    loadChildren: () =>
      import('./modules/impersonate-user/impersonate-user.module').then(
        (m) => m.ImpersonateUserModule
      ),
  },
  {
    path: 'end-impersonation-mode',
    loadChildren: () =>
      import('./modules/end-impersonation-mode/end-impersonation-mode.module').then(
        (m) => m.EndImpersonationModeModule
      ),
  },
  {
    path: 'global-admin',
    component: LayoutAdminComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    data: {
      breadcrumb: 'Home',
      appSurface: 'admin',
    },
    canActivate: [CanActivateSuperAdminRoutesGuard],
    canActivateChild: [CanActivateSuperAdminRoutesGuard],
    loadChildren: () =>
      import('./modules/global-admin/global-admin.module').then(
        (m) => m.GlobalAdminModule
      ),
  },
  {
    path: 'admin',
    component: LayoutAdminComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    canActivate: [CanActivateAdminRoutesGuard],
    // Navigation closes every drawer; one holding unsaved work (the email editor) is asked first.
    canActivateChild: [CanActivateAdminRoutesGuard, UnsavedDrawerContentGuard],
    canDeactivate: [UnsavedDrawerContentGuard],
    data: {
      breadcrumb: 'Home',
      appSurface: 'admin',
    },
    children: [
      {
        path: 'change-password',
        data: {
          breadcrumb: 'Change Password',
        },
        component: ChangePasswordComponent,
      },
      {
        path: 'settings',
        data: {
          breadcrumb: 'Settings',
        },
        component: SettingComponent,
      },
      // Super-admin company (companyId === 1) lands on Global Admin; everyone else on the
      // dashboard. Uses a functional redirect resolved against the async app context, because
      // Angular forbids combining `redirectTo` with `canMatch`/`canActivate` (NG04014 — redirects
      // are evaluated before guards run).
      {
        path: '',
        pathMatch: 'full',
        redirectTo: () => {
          const router = inject(Router);
          return inject(ApplicationContextService).context.pipe(
            map(ctx => router.parseUrl(
              ctx.userPermissions?.companyId === 1 ? '/global-admin/companies' : '/admin/dashboardv2',
            )),
            catchError(() => of(router.parseUrl('/admin/dashboardv2'))),
          );
        },
      },
      {
        path: 'quick-pricer',
        data: {
          breadcrumb: 'Quick Pricer',
        },
        loadChildren: () =>
          import('./modules/quick-pricer/quick-pricer.module').then(
            (m) => m.QuickPricerModule
          ),
      },
      {
        path: 'reports',
        data: {
          breadcrumb: 'Reporting Hub',
          reportScope: 'global',
        },
        canActivate: [CanActivateSuperAdminRoutesGuard],
        canActivateChild: [CanActivateSuperAdminRoutesGuard],
        loadChildren: () =>
          import('./modules/reports/reports-hub/reports-hub.module').then(
            (m) => m.ReportsHubModule
          ),
      },
      {
        path: 'billing-report',
        data: {
          breadcrumb: 'Billing Report',
        },
        loadChildren: () =>
          import('./modules/reports/billing-report/billing-report.module').then(
            (m) => m.BillingReportModule
          ),
      },
      {
        path: 'social-media-interactions',
        data: {
          breadcrumb: 'Social Media Interactions',
        },
        loadChildren: () =>
          import(
            './modules/social-media-interactions/social-media-interactions.module'
          ).then((m) => m.SocialMediaInteractionsModule),
      },
      {
        path: 'contact-lists',
        data: {
          breadcrumb: 'Contact Lists',
        },
        loadChildren: () =>
          import('./modules/contact-lists/contact-lists.module').then(
            (m) => m.ContactListsModule
          ),
      },
      {
        path: 'applications',
        data: {
          breadcrumb: 'My Applications',
        },
        loadChildren: () =>
          import('./modules/applications/applications.module').then(
            (m) => m.ApplicationsModule
          ),
      },
      {
        path: 'applications/new',
        data: {
          breadcrumb: 'New Application',
        },
        loadChildren: () =>
          import('./modules/new-application/new-application.module').then(
            (m) => m.NewApplicationModule
          ),
      },
      {
        path: 'app-details/:id',
        loadChildren: () =>
          import('./modules/app-details/app-details.module').then(
            (m) => m.AppDetailsModule
          ),
      },
      {
        path: 'borrowers',
        data: {
          breadcrumb: 'Borrowers',
        },
        loadChildren: () =>
          import('./modules/borrower/borrower.module').then(
            (m) => m.BorrowerModule
          ),
      },
      {
        path: 'export-documents/:id',
        data: {
          breadcrumb: 'Export Documents',
        },
        loadChildren: () =>
          import('./modules/export-loan-docs/export-loan-docs.module').then(
            (m) => m.ExportLoanDocsModule
          ),
      },
      {
        path: 'dashboardv2',
        data: {
          breadcrumb: 'Dashboard',
        },
        loadChildren: () =>
          import('./modules/dashboardv2/dashboardv2.module').then(
            (m) => m.DashboardV2Module
          ),
      },
      {
        path: 'pipeline',
        data: {
          breadcrumb: 'Pipeline',
        },
        loadChildren: () =>
          import('./modules/admin-pipeline/admin-pipeline.module').then(
            (m) => m.AdminPipelineModule
          ),
      },
      {
        path: 'command-center',
        data: {
          breadcrumb: 'Command Center',
        },
        canActivate: [CanActivateCommandCenterGuard],
        loadChildren: () =>
          import('./modules/command-center/command-center.module').then(
            (m) => m.CommandCenterModule
          ),
      },
      {
        path: 'profile/:id',
        data: {
          breadcrumb: 'Profile',
        },
        canActivate: [],
        loadChildren: () =>
          import('./modules/profile/profile.module').then(
            (m) => m.ProfileModule
          ),
      },
      {
        path: 'profile',
        data: {
          breadcrumb: 'Profile',
        },
        canActivate: [],
        loadChildren: () =>
          import('./modules/profile/profile.module').then(
            (m) => m.ProfileModule
          ),
      },
      {
        path: 'setup-profile',
        canDeactivate: [PendingChangesGuard],
        data: {
          breadcrumb: 'Setup Profile',
        },
        component: SetupProfileComponent,
      },
      // Declared before 'company' so it wins the match for /admin/company/reports:
      // the Reporting header link must stay reachable by every user, so the reports
      // hub lives outside the admin-only 'company' route and each tile carries the
      // permission checks the old Reporting submenu applied.
      {
        path: 'company/reports',
        data: {
          breadcrumb: 'Reporting Hub',
          reportScope: 'company',
        },
        loadChildren: () =>
          import('./modules/reports/reports-hub/reports-hub.module').then(
            (m) => m.ReportsHubModule
          ),
      },
      {
        path: 'company',
        data: {
          breadcrumb: 'Company',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/company/company.module').then(
            (m) => m.CompanyModule
          ),
      },
      {
        path: 'external-companies',
        data: {
          breadcrumb: 'External Companies',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/external-company-management/external-company-management.module'
          ).then((m) => m.ExternalCompanyManagementModule),
      },
      {
        path: 'loan-config',
        data: {
          breadcrumb: 'Loan Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/loan-config/loan-config.module').then(
            (m) => m.LoanConfigModule
          ),
      },
      {
        path: 'los-config',
        data: {
          breadcrumb: 'LOS Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/los-config/los-config.module').then(
            (m) => m.LosConfigModule
          ),
      },
      {
        path: 'ocr-config',
        data: {
          breadcrumb: 'OCR Config',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/ocr-config/ocr-config.module').then(
            (m) => m.OcrConfigModule
          ),
      },
      {
        path: 'lead-config',
        data: {
          breadcrumb: 'Lead Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/lead-config/lead-config.module').then(
            (m) => m.LeadConfigModule
          ),
      },
      {
        path: 'short-links',
        data: {
          breadcrumb: 'Route Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/short-links/short-links.module').then(
            (m) => m.ShortLinksModule
          ),
      },
      {
        path: 'brand-configuration',
        data: {
          breadcrumb: 'Brand Configuration',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/brand-configuration/brand-configuration.module').then(
            (m) => m.BrandConfigurationModule
          ),
      },
      {
        path: 'external-brand-configuration',
        data: {
          breadcrumb: 'External Brand Configuration',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/external-brand-configuration/external-brand-configuration.module').then(
            (m) => m.ExternalBrandConfigurationModule
          ),
      },
      {
        path: 'task-config',
        data: {
          breadcrumb: 'Task Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/task-config/task-config.module').then(
            (m) => m.TaskConfigModule
          ),
      },
      {
        path: 'contacts',
        data: {
          breadcrumb: 'Contacts',
        },
        loadChildren: () =>
          import('./modules/contacts/contacts.module').then(
            (m) => m.ContactsModule
          ),
      },
      {
        path: 'external-organizations',
        data: {
          breadcrumb: 'External Organizations',
        },
        loadChildren: () =>
          import('./modules/external-organizations/external-organizations.module').then(
            (m) => m.ExternalOrganizationsModule
          ),
      },
      {
        path: 'external-users',
        data: {
          breadcrumb: 'External Org. Users',
        },
        loadChildren: () =>
          import('./modules/external-org-users/external-org-users.module').then(
            (m) => m.ExternalOrgUsersModule
          ),
      },
      {
        path: 'remain-contact/:appId',
        data: {
          breadcrumb: 'Remain Contact',
        },
        loadChildren: () =>
          import('./modules/remain-contact/remain-contact.module').then(
            (m) => m.RemainContactModule
          ),
      },
      {
        path: 'leads',
        data: {
          breadcrumb: 'Leads',
        },
        loadChildren: () =>
          import('./modules/leads/leads.module').then((m) => m.LeadsModule),
      },
      {
        path: 'dialer',
        data: {
          breadcrumb: 'Dialer',
        },
        loadChildren: () =>
          import('./modules/dialer/dialer.module').then((m) => m.DialerModule),
      },
      {
        path: 'agents',
        data: {
          breadcrumb: 'Agents',
        },
        loadChildren: () =>
          import('./modules/agents/agents.module').then((m) => m.AgentsModule),
      },
      {
        path: 'calendar',
        data: {
          breadcrumb: 'Calendar',
        },
        loadChildren: () =>
          import('./modules/events/events.module').then((m) => m.EventsModule),
      },
      {
        path: 'system-level',
        data: {
          breadcrumb: 'System Level',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/system-level/system-level.module').then(
            (m) => m.SystemLevelModule
          ),
      },
      {
        path: 'application-settings',
        data: {
          breadcrumb: 'Application Settings',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/application-settings/application-settings.module').then(
            (m) => m.ApplicationSettingsModule
          ),
      },
      {
        path: 'search',
        data: {
          breadcrumb: 'Search',
        },
        loadChildren: () =>
          import('./modules/search/search.module').then((m) => m.SearchModule),
      },
      {
        path: 'configuration-issues',
        data: {
          breadcrumb: 'Configuration Issues',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/configuration-issues/configuration-issues.module'
          ).then((m) => m.ConfigurationIssuesModule),
      },
      {
        path: 'webhook-subscription',
        data: {
          breadcrumb: 'Webhook Subscriptions',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/webhook-subscription/webhook-subscription.module'
          ).then((m) => m.WebhookSubscriptionModule),
      },
      {
        path: 'tag-management',
        data: {
          breadcrumb: 'Tag Management',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/tag-management/tag-management.module'
          ).then((m) => m.TagManagementModule),
      },
      {
        path: 'agentic-profiles',
        data: {
          breadcrumb: 'Agentic Profiles',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/agentic-profiles/agentic-profiles.module'
          ).then((m) => m.AgenticProfilesModule),
      },
      {
        path: 'agentic-workflows',
        data: {
          breadcrumb: 'Agentic Workflows',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/agentic-workflow-admin/agentic-workflow-admin.module'
          ).then((m) => m.AgenticWorkflowAdminModule),
      },
      {
        path: 'underwriting-configuration',
        data: {
          breadcrumb: 'Underwriting Configuration',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/underwriting-config/underwriting-config.module'
          ).then((m) => m.UnderwritingConfigModule),
      },
      {
        path: 'guardrails',
        data: {
          breadcrumb: 'Guardrails',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/guardrails/guardrails.module').then(
            (m) => m.GuardrailsModule
          ),
      },
      {
        path: 'evals',
        data: {
          breadcrumb: 'Evals',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/evals/evals.module').then((m) => m.EvalsModule),
      },
      {
        path: 'tpo-config',
        data: {
          breadcrumb: 'TPO Config',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/tpo-config/tpo-config.module').then(
            (m) => m.TpoConfigModule
          ),
      },
      {
        path: 'checklists',
        data: {
          breadcrumb: 'Checklists',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/checklists/checklists.module').then(
            (m) => m.ChecklistsModule
          ),
      },
      {
        path: 'reporting-config',
        data: {
          breadcrumb: 'Reporting Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/reporting-config/reporting-config.module'
          ).then((m) => m.ReportingConfigModule),
      },
      {
        path: 'lender-config',
        data: {
          breadcrumb: 'Lender Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/lender-config/lender-config.module').then(
            (m) => m.LenderConfigModule
          ),
      },
      {
        path: 'fee-override-config',
        data: {
          breadcrumb: 'Fee Override Config',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/system-admin/system-admin.module').then(
            (m) => m.SystemAdminModule
          ),
      },
      {
        path: 'solar-config',
        data: {
          breadcrumb: 'Solar Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/solar-config/solar-config.module').then(
            (m) => m.SolarConfigModule
          ),
      },
      {
        path: 'loan-beam-config',
        data: {
          breadcrumb: 'Loan Beam Config',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/loan-beam-config/loan-beam-config.module'
          ).then((m) => m.LoanBeamConfigModule),
      },
      {
        path: 'document-config',
        data: {
          breadcrumb: 'Document Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/document-config/document-config.module').then(
            (m) => m.DocumentConfigModule
          ),
      },

      {
        path: 'task-metrics',
        data: {
          breadcrumb: 'Task Metrics',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import(
            './modules/reports/task-metrics/task-metrics-report.module'
          ).then((m) => m.TaskMetricsReportModule),
      },
      {
        path: 'sales-report',
        data: {
          breadcrumb: 'Sales Report',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import('./modules/reports/sales-report/sales-report.module').then(
            (m) => m.SalesReportModule
          ),
      },
      {
        path: 'warm-transfer-config',
        data: {
          breadcrumb: 'Warm Transfer Config',
        },
        loadChildren: () =>
          import(
            './modules/admin/warm-transfer-config/warm-transfer-config.module'
          ).then((m) => m.WarmTransferConfigModule),
      },
      {
        path: 'pipeline-metrics',
        data: {
          breadcrumb: 'Pipeline Metrics',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        canActivate: [CanActivatePipelineMetricsGuard],
        canActivateChild: [CanActivatePipelineMetricsGuard],
        loadChildren: () =>
          import('./modules/reports/pipeline-metrics/pipeline-metrics-report.module')
            .then((m) => m.PipelineMetricsReportModule),
      },
      {
        path: 'pipeline-waterfall',
        data: {
          breadcrumb: 'Pipeline Waterfall',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        canActivate: [CanActivatePipelineWaterfallGuard],
        canActivateChild: [CanActivatePipelineWaterfallGuard],
        loadChildren: () =>
          import('./modules/reports/pipeline-waterfall/pipeline-waterfall-report.module')
            .then((m) => m.PipelineWaterfallReportModule),
      },
      {
        path: 'agent-dashboard',
        data: {
          breadcrumb: 'Dialer Dashboard',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import(
            './modules/reports/dialer-dashboard/dialer-dashboard.module'
          ).then((m) => m.DialerDashboardModule),
      },
      {
        path: 'dialer-config',
        data: {
          breadcrumb: 'Voice / SMS Config',
          isDisabled: true,
        },
        canActivate: [CanActivateDncListAccessGuard],
        canActivateChild: [CanActivateDncListAccessGuard],
        loadChildren: () =>
          import('./modules/admin/dialer-config/dialer-config.module').then(
            (m) => m.DialerConfigModule
          ),
      },
      {
        path: 'agent-config',
        data: {
          breadcrumb: 'Agent Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/admin/agent-config/agent-config.module').then(
            (m) => m.AgentConfigModule
          ),
      },
      {
        path: 'email-campaign-config',
        data: {
          breadcrumb: 'Email Config',
          isDisabled: true,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/admin/email-campaign-config/email-campaign-config.module'
          ).then((m) => m.EmailCampaignConfigModule),
      },
      {
        path: 'reporting-dashboard',
        data: {
          breadcrumb: 'Reporting Dashboard',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import(
            './modules/reports/reporting-dashboard/reporting-dashboard.module'
          ).then((m) => m.ReportingDashboardModule),
      },
      {
        path: 'kpi-dashboard',
        data: {
          breadcrumb: 'KPI Dashboard',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import('./modules/reports/kpi-dashboard/kpi-dashboard.module').then(
            (m) => m.KpiDashboardModule
          ),
      },
      {
        path: 'campaigns',
        data: {
          breadcrumb: 'Campaigns',
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import('./modules/campaign/campaign.module').then(
            (m) => m.CampaignModule
          ),
      },
      {
        path: 'broadcast-manager',
        data: {
          breadcrumb: 'Broadcast Manager',
        },
        canActivate: [CanActivateBroadcastManagerRoutesGuard],
        canActivateChild: [CanActivateBroadcastManagerRoutesGuard],
        loadChildren: () =>
          import('./modules/broadcast-manager/broadcast-manager.module').then(
            (m) => m.BroadcastManagerModule
          ),
      },
      {
        path: 'support-tickets',
        data: {
          breadcrumb: 'Support Tickets',
        },
        loadChildren: () =>
          import('./modules/support-ticket-management/support-ticket-management.module').then(
            (m) => m.SupportTicketManagementModule
          ),
      },
      {
        path: 'my-compensation',
        data: {
          breadcrumb: 'My Compensation',
        },
        loadChildren: () =>
          import('./modules/my-compensation/my-compensation.module').then(
            (m) => m.MyCompensationModule
          ),
      },
      {
        path: 'mortgage-call',
        data: {
          breadcrumb: 'Mortgage Call',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import(
            './modules/reports/mortgage-call/mortgage-call-report.module'
          ).then((m) => m.MortgageCallReportModule),
      },
      {
        path: 'profitability-report',
        data: {
          breadcrumb: 'Profitability Report',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/reports/profitability-report/profitability-report.module'
          ).then((m) => m.ProfitabilityReportModule),
      },
      {
        path: 'loan-compensation-report',
        data: {
          breadcrumb: 'Loan Officer Compensation',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        canActivate: [CanActivateAdminRoleRoutesGuard],
        canActivateChild: [CanActivateAdminRoleRoutesGuard],
        loadChildren: () =>
          import(
            './modules/reports/loan-compensation-report/loan-compensation-report.module'
          ).then((m) => m.LoanCompensationReportModule),
      },
      {
        path: 'lead-campaign-performance',
        data: {
          breadcrumb: 'Lead Campaign Performance',
        },
        loadChildren: () =>
          import(
            './modules/reports/lead-campaign-performance/lead-campaign-performance.module'
          ).then((m) => m.LeadCampaignPerformanceModule),
      },
      {
        path: 'performance-report',
        data: {
          breadcrumb: 'Performance Report',
        },
        loadChildren: () =>
          import(
            './modules/reports/performance-report/performance-report.module'
          ).then((m) => m.PerformanceReportModule),
      },
      {
        path: 'formfree-takeownership',
        loadChildren: () =>
          import('./modules/take-ownership/take-ownership.module').then(
            (m) => m.TakeOwnershipModule
          ),
      },
      {
        path: 'esign-via-sms/:loanDocTaskId',
        loadChildren: () =>
          import('./modules/esign-email-sms/esign-email-sms.module').then(
            (m) => m.EsignEmailSmsModule
          ),
      },
      {
        path: 'esign-via-email/:loanDocTaskId',
        loadChildren: () =>
          import('./modules/esign-email-sms/esign-email-sms.module').then(
            (m) => m.EsignEmailSmsModule
          ),
      },
      {
        path: 'wizard',
        data: {
          breadcrumb: 'Wizard',
        },
        loadChildren: () =>
          import('./modules/admin/wizard/wizard.module').then(
            (m) => m.WizardModule
          ),
      },
      {
        path: 'messaging-report',
        data: {
          breadcrumb: 'Messaging report',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import(
            './modules/reports/messaging-report/messaging-report.module'
          ).then((m) => m.MessagingReportModule),
      },
      {
        path: 'vici-dialer-report',
        data: {
          breadcrumb: 'VICI Dialer report',
          breadcrumbParent: reportsHubBreadcrumbParent,
        },
        loadChildren: () =>
          import(
            './modules/reports/vici-dialer-report/vici-dialer-report.module'
          ).then((m) => m.ViciDialerReportModule),
      },
      {
        path: 'communications-report',
        data: {
          breadcrumb: 'Communications Report',
        },
        loadChildren: () =>
          import(
            './modules/reports/communications-report/communications-report.module'
          ).then((m) => m.CommunicationsReportModule),
      },
      {
        path: 'contact-list-details',
        data: {
          breadcrumb: 'Contact List Details',
        },
        loadChildren: () =>
          import('./modules/contact-list-details/contact-list-details.module').then((m) => m.ContactListDetailsModule),
      },
      {
        path: 'outbound-dial-report',
        data: {
          breadcrumb: 'Outbound Dial Report',
        },
        loadChildren: () =>
          import(
            './modules/reports/outbound-dial-report/outbound-dial-report.module'
          ).then((m) => m.OutboundDialReportModule),
      },
    ],
  },
  {
    path: 'tpo',
    canActivate: [CanActivateTpoRoutesGuard],
    canActivateChild: [CanActivateTpoRoutesGuard],
    data: {
      breadcrumb: 'TPO',
      appSurface: 'tpo',
    },
    component: TpoLayoutComponent,
    resolve: {
      applicationContext: ApplicationContextResolver,
    },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'settings',
        data: {
          breadcrumb: 'Settings',
        },
        component: TpoSettingsComponent,
      },
      {
        path: 'change-password',
        data: {
          breadcrumb: 'Change Password',
        },
        component: TpoChangePasswordComponent,
      },
      {
        path: 'esign-via-email/:loanDocTaskId',
        loadChildren: () =>
          import('./modules/esign-email-sms/esign-email-sms.module').then(
            (m) => m.EsignEmailSmsModule
          ),
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import(
            './modules/tpo/modules/tpo-dashboard/tpo-dashboard.module'
          ).then((m) => m.TpoDashboardModule),
        data: {
          breadcrumb: 'Dashboard',
        },
      },
      {
        path: 'new-application',
        component: TpoNewApplicationComponent,
        data: {
          breadcrumb: 'New Application',
        },
      },
      {
        path: 'new-lead-referral',
        component: LeadEditorComponent,
        data: {
          breadcrumb: 'New Lead Referral',
        },
      },
      {
        path: 'pipeline',
        component: TpoPipelineComponent,
        data: {
          breadcrumb: 'Pipeline',
        },
      },
      {
        path: 'support',
        component: BrokerSupportComponent,
        data: {
          breadcrumb: 'Broker Support',
          view: 'landing',
        },
      },
      {
        path: 'support/help',
        component: BrokerSupportComponent,
        data: {
          breadcrumb: 'Broker Support',
          view: 'help',
        },
      },
      {
        path: 'support/resources',
        component: BrokerSupportComponent,
        data: {
          breadcrumb: 'Broker Support',
          view: 'resources',
        },
      },
      {
        path: 'support/resources/:slug',
        component: BrokerSupportComponent,
        data: {
          breadcrumb: 'Broker Support',
          view: 'resources',
        },
      },
      {
        // Legacy deep link. Redirect external bookmarks of /tpo/support/page/<slug>
        // into the new inline resources preview so the same slug pre-selects there.
        path: 'support/page/:slug',
        redirectTo: 'support/resources/:slug',
        pathMatch: 'full',
      },
      {
        path: 'quick-pricer',
        component: TpoQuickPricerComponent,
        canActivate: [HasPpeAccessGuard],
        data: {
          breadcrumb: 'Quick Pricer',
        },
      },
      {
        path: 'appraisal-management',
        component: TpoAppraisalManagementComponent,
        data: {
          breadcrumb: 'Appraisal Management',
        },
      },
      {
        path: 'va-payment',
        component: TpoVaPaymentComponent,
        data: {
          breadcrumb: 'VA Payment',
        },
      },
      {
        path: 'app-details/:id',
        component: TpoAppDetailsComponent,
        canActivateChild: [CanActivateDuringDisclosureProcessingGuard],
        children: [
          {
            path: 'urla',
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'URLA',
              menuKey: 'Urla'
            },
            loadChildren: () =>
              import('./modules/urla/urla.module').then((m) => m.UrlaModule),
          },
          {
            path: 'credit-reports',
            component: LoanCreditComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PendingChangesGuard],
            data: {
              breadcrumb: 'Credit Reports',
              menuKey: 'Credit'
            },
          },
          {
            path: 'loan-summary',
            component: TpoLoanSummaryComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PendingChangesGuard],
            data: {
              breadcrumb: 'Loan Summary',
              menuKey: 'LoanSummary'
            },
          },
          {
            path: 'tpo-loan-summary',
            component: TpoLoanSummaryV2Component,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PendingChangesGuard],
            data: {
              breadcrumb: 'Loan Summary',
              menuKey: 'LoanSummary'
            },
          },
          {
            path: 'digital-emp-income-verification',
            component: TwnComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Digital Employment Verification',
              menuKey: 'VoiVoe'
            },
          },
          {
            path: 'digital-asset-verification',
            component: VoaComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Digital Asset Verification',
              menuKey: 'Voa'
            },
          },
          {
            path: 'aus',
            component: AusComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Automated Underwriting',
              menuKey: 'Aus'
            },
          },
          {
            path: 'mi-quoting',
            component: MiQuotingComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'PMI Quote',
              menuKey: 'MiQuote'
            },
          },
          {
            path: 'link', loadChildren: () =>
              import('./modules/loan-services/link/link-trio.module').then((m) => m.LinkTrioModule),
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              menuKey: 'Link'
            }
          },

          {
            path: 'link-income-calculator', loadChildren: () =>
              import('./modules/loan-services/link-income-calculator/link-income-calculator.module').then((m) => m.LinkIncomeCalculatorModule),
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              menuKey: 'LinkIncomeCalc'
            }

          },
          {
            path: 'income-analysis', loadChildren: () =>
              import('./modules/loan-services/income-analysis/income-analysis.module').then((m) => m.IncomeAnalysisModule),
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              menuKey: 'IncomeAnalysis'
            }
          },
          {
            path: 'disclosures',
            loadChildren: () =>
              import('./modules/loan-services/run-disclosures/run-disclosures.module').then((m) => m.RunDisclosuresModule),
            canActivate: [CanActivatePmiQuoteBeforeDisclosuresGuard],
          },
          {
            path: 'disclosure',
            component: DisclosuresWizardComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard, CanActivatePmiQuoteBeforeDisclosuresGuard],
            data: {
              breadcrumb: 'Disclosures',
              menuKey: 'Disclosures'
            },
          },
          {
            path: 'appraisal',
            component: AppraisalComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Appraisal',
              menuKey: 'Appraisal'
            },
          },
                    {
                      path: 'cd-request',
                      component: TpoCdRequestComponent,
                      data: {
                        breadcrumb: 'CD Request',
                      },
                    },
                    {
                      path: 'corr-icd-request',
                      component: TpoCorrIcdRequestComponent,
                      data: {
                        breadcrumb: 'ICD Request',
                      },
                    },
                    {
                      path: 'manual-lock',
            component: TpoManualLockComponent,
            data: {
              breadcrumb: 'Manual Lock',
            },
          },
          {
            path: 'conditions', component: ConditionsTasksComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PendingChangesGuard],
            data: {
              breadcrumb: 'Conditions',
              menuKey: 'Conditions'
            },
          },
          {
            path: 'fees',
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PendingChangesGuard],
            component: LoanFeesComponent,
            data: {
              breadcrumb: 'Fees',
            },
          },
          {
            path: 'fees/feewise-wizard/complete',
            component: LoanFeesComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Fees',
              menuKey: 'Fees'
            },
          },
          {
            path: 'atr-qm-management',
            component: AtrQmManagementComponent,
            data: {
              breadcrumb: 'ATR / QM Management',
            },
          },
          {
            path: 'pricing',
            component: PricingComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PricingIFrameChangesGuard],
            data: {
              breadcrumb: 'Pricing',
              menuKey: 'Pricing'
            },
          },
          {
            path: 'edit-pricing',
            loadChildren: () =>
              import('./modules/edit-pricing/edit-pricing.module').then(
                (m) => m.EditPricingModule
              ),
            data: {
              breadcrumb: 'Pricing',
            },
          },
          {
            path: 'pricing-history',
            component: PricingHistoryTableComponent,
            data: {
              breadcrumb: 'Pricing History',
            },
          },
          {
            path: 'application-pricing-scenarios',
            component: ApplicationPricingScenariosComponent,
            data: {
              breadcrumb: 'Pricing Scenarios',
            },
          },
          {
            path: 'amortization',
            component: AmortizationComponent,
            data: {
              breadcrumb: 'Amortization',
            },
          },
          {
            path: 'submission',
            component: SubmissionComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            canDeactivate: [PendingChangesGuard],
            data: {
              breadcrumb: 'Submission',
              menuKey: 'Submission'
            },
          },
          {
            path: 'uw-decision',
            component: UWDecisionComponent,
            data: {
              breadcrumb: 'UW Decision',
            },
          },
          {
            path: 'key-dates',
            component: KeyDatesComponent,
            canDeactivate: [PendingChangesGuard],
            data: {
              breadcrumb: 'Key Dates',
            },
          },
          {
            path: 'file-contacts',
            component: FileContactsComponent,
            data: {
              breadcrumb: 'File Contacts',
            },
          },
          {
            path: 'doc-preparation',
            component: DocumentPreparationComponent,
            data: {
              breadcrumb: 'Document Preparation',
            },
          },
          {
            path: 'internal-contacts',
            component: InternalContactsComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Internal Contacts',
              menuKey: 'Internal'
            },
          },
          {
            path: 'external-contacts',
            component: ExternalContactsV2Component,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'External Contacts',
              menuKey: 'External'
            },
          },
          {
            path: 'associate-file-contact',
            component: RemainContactComponent,
          },
          {
            path: 'loan-activity',
            component: LoanActivityCardComponent,
            data: {
              breadcrumb: 'Loan Activity',
            },
          },
          {
            path: 'escalation-history',
            component: EscalationHistoryComponent,
            data: {
              breadcrumb: 'Escalation History',
            },
          },
          {
            path: 'loan-docs-v3',
            loadChildren: () =>
              import('./modules/loan-docs-v3/loan-docs-v3.module').then(
                (m) => m.LoanDocsV3Module
              ),
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Loan Documents',
              menuKey: 'LoanDocs'
            },
          },
          {
            path: 'change-of-circumstance',
            component: ChangeOfCircumstanceComponent,
            canActivate: [CanActivateDisabledTpoMenuItemGuard],
            data: {
              breadcrumb: 'Change of Circumstance',
              menuKey: 'ChangeOfCircumstance'
            },
          },
          { path: 'amortization', component: AmortizationComponent },
          {
            path: 'export-documents',
            data: {
              breadcrumb: 'Export Documents',
            },
            loadChildren: () =>
              import('./modules/export-loan-docs/export-loan-docs.module').then(
                (m) => m.ExportLoanDocsModule
              ),
          },
          {
            path: 'esignature-history',
            component: TpoEsignatureHistoryComponent,
          }
        ],
      },
      {
        path: 'remain-contact/:appId',
        data: {
          breadcrumb: 'Remain Contact',
        },
        loadChildren: () =>
          import('./modules/remain-contact/remain-contact.module').then(
            (m) => m.RemainContactModule
          ),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./modules/tpo/modules/tpo-profile/tpo-profile.module').then(
            (m) => m.TpoProfileModule
          ),
        data: {
          breadcrumb: 'Profile',
        },
      }
    ],
  },
  {
    path: 'iframe-confirmation/document-signing/signnow',
    component: DocumentSigningRequestConfirmationComponent,
    data: { passReturnParamsToParent: true },
  },
  {
    path: 'iframe-confirmation/document-signing',
    component: DocumentSigningRequestConfirmationComponent,
  },
  {
    path: 'iframe-confirmation/document-esigning',
    component: DocumentESigningRequestConfirmationComponent,
  },
  // The api pins this url - host included - into signing orders whose vendor cannot be handed a return url per
  // signer, so it is served from the app root rather than under an admin/tpo path.
  {
    path: 'e-sign-vendor-confirmation',
    component: ESignVendorConfirmationComponent,
  },
  {
    path: 'epc-partner',
    loadChildren: () => import('./modules/epc-partner/epc-partner.module').then(m => m.EpcPartnerModule),
  },

  { path: 'admin/esign-via-token', component: ESignViaTokenComponent },
  { path: 'admin/esign-via-token/:loanDocTaskId', component: ESignViaTokenComponent },
  { path: 'admin/esign-confirmation/:token/:taskNumber/:allTasksCount/:orderId', component: ESignViaTokenConfirmationComponent },
  { path: 'tpo/esign-via-token', component: ESignViaTokenComponent },
  { path: 'tpo/esign-via-token/:loanDocTaskId', component: ESignViaTokenComponent },
  { path: 'tpo/esign-confirmation/:token/:taskNumber/:allTasksCount/:orderId', component: ESignViaTokenConfirmationComponent },


  { path: 'index', redirectTo: 'admin' },
  { path: '', redirectTo: 'admin', pathMatch: 'full' },
  { path: '**', component: LoginComponent },
];
@NgModule({
  imports: [RouterModule.forRoot(routes, { 
    onSameUrlNavigation: 'reload', 
    // A navigation a guard cancels (staying in an editor with unsaved changes, say) puts the browser history back
    // where it was, so Back and Forward still lead where they did.
    canceledNavigationResolution: 'computed',
    enableTracing: false,
    preloadingStrategy: PreloadAllModules  // Preload all lazy modules in background after initial load
  })],
  exports: [RouterModule],
})
export class AppRoutingModule { }
